import { useGlobalSearchParams } from 'expo-router';
import { forwardRef } from 'react';
import { View } from 'react-native';

import { Text, VStack, theme } from '@squire/ui';

import { TagIcon } from '@/components/_icons';
import { useScreenTranslations } from '@/hooks/useScreenTranslations';
import { DateFormat, formatDateWithTimezone } from '@/utils/dateUtils';
import { formatPrice } from '@/utils/internationalizationUtils';

import { styles } from './styles';
import type { ViewOrderCartPromoProps } from './types';

const ViewOrderCartPromo = forwardRef<View, ViewOrderCartPromoProps>(
  ({ promo, testID, timezone, currency, ...props }, ref) => {
    const { t, i18n } = useScreenTranslations('ViewOrderCartPromo');
    const { promoId } = useGlobalSearchParams<{ promoId: string }>();

    const endDate = promo?.rules.find((rule) => rule.kind === 2)?.endDate;

    const getSavingAmount = () => {
      if (!promo) return '';
      if (promo.kind === 1) {
        return `${promo.value}%`;
      }

      return formatPrice(promo.value / 100, {
        currency,
        showCurrency: true,
        locale: i18n.language,
      });
    };

    if (!promo || !promoId) return null;
    return (
      <View ref={ref} style={styles.container} testID={testID} {...props}>
        <VStack space={theme.spacing[1]} style={styles.textContainer}>
          <Text variant="mStrong" style={styles.savingText}>
            {t('saving', { amount: getSavingAmount() })}
          </Text>
          {endDate && (
            <Text variant="m" style={styles.dateText}>
              {t('useBy', {
                date: formatDateWithTimezone(
                  endDate,
                  DateFormat.DATE,
                  timezone,
                ),
              })}
            </Text>
          )}
        </VStack>
        <View style={styles.iconContainer}>
          <TagIcon color="iconPositive" size={16} />
        </View>
      </View>
    );
  },
);

ViewOrderCartPromo.displayName = 'ViewOrderCartPromo';
export { ViewOrderCartPromo };
